// src/app/work-order-numbers/services/work-order-file-parser.service.ts
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import * as XLSX from 'xlsx';

import { WorkOrderImportRow } from '../models/work-order-import-row.model';
import { WORK_ORDER_IMPORT_HEADERS } from '../models/work-order-import-headers.const';

@Injectable({ providedIn: 'root' })
export class WorkOrderFileParserService {
  parse(file: File): Observable<WorkOrderImportRow[]> {
    return new Observable<WorkOrderImportRow[]>((observer) => {
      try {
        this.validateFileType(file);

        const reader = new FileReader();

        reader.onerror = () => observer.error(new Error('Failed to read file'));

        reader.onload = (e: any) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });

            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];

            const rawData = XLSX.utils.sheet_to_json<any[]>(worksheet, {
              header: 1,
              defval: null,
              blankrows: false,
            });

            if (!rawData.length) throw new Error('Uploaded file is empty');

            this.validateHeaders(rawData[0]);

            const rows = this.mapRows(rawData.slice(1));
            observer.next(rows);
            observer.complete();
          } catch (err) {
            observer.error(err);
          }
        };

        reader.readAsArrayBuffer(file);
      } catch (err) {
        observer.error(err);
      }
    });
  }

  private validateFileType(file: File): void {
    const allowed = ['xlsx', 'xls', 'csv'];
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !allowed.includes(extension)) {
      throw new Error('Invalid file type. Allowed: xlsx, xls, csv');
    }
  }

  private validateHeaders(headers: any[]): void {
    if (headers.length !== WORK_ORDER_IMPORT_HEADERS.length) {
      throw new Error('Invalid template: column count mismatch');
    }

    headers.forEach((header, index) => {
      if (header !== WORK_ORDER_IMPORT_HEADERS[index]) {
        throw new Error(
          `Invalid template: expected "${WORK_ORDER_IMPORT_HEADERS[index]}" at column ${index + 1}`
        );
      }
    });
  }

  private mapRows(rows: any[][]): WorkOrderImportRow[] {
    return rows
      .map((row) => {
        const record: any = {};
        WORK_ORDER_IMPORT_HEADERS.forEach((header, colIndex) => {
          record[header] = this.normalizeValue(row[colIndex]);
        });
        return record as WorkOrderImportRow;
      })
      .filter((r) => Object.values(r).some((v) => v !== null)); // drop completely empty rows
  }

  private normalizeValue(value: any): any {
    if (value === undefined || value === '') return null;
    return value;
  }
}